import {
  buildKeyWraps,
  changePassphraseWraps,
  createEnvelope,
  formatRecoveryKey,
  generateDek,
  generateRecoveryKey,
  HybridCryptoError,
  openEnvelope,
  rotateRecoveryKeyWraps,
  type HybridEnvelope,
  type HybridKeyWraps,
} from './HybridBackupCrypto';
import { getHybridKeyStore, type HybridStoredConfig } from './HybridKeyStore';

/**
 * États exposés à l'interface :
 *  - unavailable : coffre sécurisé du poste indisponible (aucun envoi, fail-closed) ;
 *  - setup_required : aucune phrase secrète configurée (aucun envoi) ;
 *  - recovery_key_unconfirmed : clé de récupération affichée, « je l'ai conservée » pas encore coché ;
 *  - ready : les sauvegardes chiffrées partent.
 */
export type HybridSetupState = 'unavailable' | 'setup_required' | 'recovery_key_unconfirmed' | 'ready';

export interface HybridSetupSnapshot {
  state: HybridSetupState;
  /** Une clé de récupération de remplacement attend sa confirmation (l'ancienne reste active). */
  replacementPending: boolean;
  configuredAt: string | null;
}

type Listener = (snapshot: HybridSetupSnapshot) => void;
const listeners = new Set<Listener>();

export function subscribeHybridSetupState(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

async function emit(): Promise<void> {
  if (listeners.size === 0) return;
  const snapshot = await HybridBackupConfigService.getSetupSnapshot();
  for (const l of listeners) {
    try {
      l(snapshot);
    } catch {
      /* un abonné défaillant ne bloque pas les autres */
    }
  }
}

function emptyConfig(): HybridStoredConfig {
  return { version: 1, active: null, pending: null, configured_at: null };
}

export class HybridBackupConfigService {
  /** Notifie les abonnés (écran, bannière) de l'état courant. */
  static announceState(): Promise<void> {
    return emit();
  }

  static async getSetupSnapshot(): Promise<HybridSetupSnapshot> {
    const store = getHybridKeyStore();
    if (!(await store.isAvailable())) {
      return { state: 'unavailable', replacementPending: false, configuredAt: null };
    }
    const { dek, config } = await store.load();
    if (!dek || !config) return { state: 'setup_required', replacementPending: false, configuredAt: null };
    if (config.active) {
      return {
        state: 'ready',
        replacementPending: config.pending !== null,
        configuredAt: config.configured_at,
      };
    }
    return {
      state: config.pending ? 'recovery_key_unconfirmed' : 'setup_required',
      replacementPending: false,
      configuredAt: null,
    };
  }

  /** Éléments nécessaires à l'envoi, ou null si l'envoi est interdit. */
  static async getSendingMaterial(): Promise<{ dek: Uint8Array; wrapping: HybridKeyWraps } | null> {
    const store = getHybridKeyStore();
    if (!(await store.isAvailable())) return null;
    const { dek, config } = await store.load();
    if (!dek || !config?.active) return null;
    return { dek, wrapping: config.active };
  }

  /**
   * Étape 1 de l'assistant : choisit la phrase secrète et génère la clé de récupération.
   * Retourne la clé AFFICHÉE UNE SEULE FOIS ; elle n'est stockée nulle part (seule son enveloppe l'est).
   * Les envois restent bloqués jusqu'à `confirmRecoveryKeyStored()`.
   */
  static async beginSetup(passphrase: string): Promise<{ recoveryKey: string }> {
    const store = getHybridKeyStore();
    if (!(await store.isAvailable())) {
      throw new HybridCryptoError('BAD_INPUT', 'Coffre sécurisé du poste indisponible.');
    }
    const material = await store.load();
    if (material.config?.active) {
      throw new HybridCryptoError('BAD_INPUT', 'La sauvegarde chiffrée est déjà configurée.');
    }
    const dek = material.dek ?? generateDek();
    const recovery = generateRecoveryKey();
    const wraps = await buildKeyWraps({ dek, passphrase, recoveryKey: recovery });
    await store.save({ dek, config: { ...emptyConfig(), pending: wraps } });
    const recoveryKey = await formatRecoveryKey(recovery);
    await emit();
    return { recoveryKey };
  }

  /** Étape 2 : l'administrateur confirme avoir conservé la clé de récupération. */
  static async confirmRecoveryKeyStored(): Promise<HybridSetupSnapshot> {
    const store = getHybridKeyStore();
    const { dek, config } = await store.load();
    if (!dek || !config?.pending) {
      throw new HybridCryptoError('BAD_INPUT', 'Aucune clé de récupération à confirmer.');
    }
    await store.save({
      dek,
      config: { version: 1, active: config.pending, pending: null, configured_at: new Date().toISOString() },
    });
    await emit();
    return HybridBackupConfigService.getSetupSnapshot();
  }

  /** Abandonne une clé de récupération non confirmée (l'ancienne, si elle existe, reste active). */
  static async cancelPending(): Promise<void> {
    const store = getHybridKeyStore();
    const { dek, config } = await store.load();
    if (!dek || !config) return;
    await store.save({ dek, config: { ...config, pending: null } });
    await emit();
  }

  /** Vérifie la phrase actuelle contre l'enveloppe active (sans rien écrire). */
  static async verifyPassphrase(passphrase: string): Promise<boolean> {
    const material = await HybridBackupConfigService.getSendingMaterial();
    if (!material) return false;
    const probe = await HybridBackupConfigService.probeEnvelope(material);
    try {
      await openEnvelope(probe, { passphrase });
      return true;
    } catch {
      return false;
    }
  }

  private static async probeEnvelope(material: { dek: Uint8Array; wrapping: HybridKeyWraps }): Promise<HybridEnvelope> {
    return createEnvelope('probe', {
      dek: material.dek,
      wrapping: material.wrapping,
      meta: { created_at: new Date(0).toISOString(), app_version: '0', schema_version: 0 },
    });
  }

  /** Change la phrase secrète (exige l'ancienne). Les anciens coffres gardent l'ancienne phrase ; la clé de récupération reste valable. */
  static async changePassphrase(currentPassphrase: string, newPassphrase: string): Promise<void> {
    const store = getHybridKeyStore();
    const { dek, config } = await store.load();
    if (!dek || !config?.active) throw new HybridCryptoError('BAD_INPUT', 'Sauvegarde chiffrée non configurée.');
    if (!(await HybridBackupConfigService.verifyPassphrase(currentPassphrase))) {
      throw new HybridCryptoError('WRONG_SECRET', 'Phrase secrète actuelle incorrecte.');
    }
    const active = await changePassphraseWraps(config.active, { dek, newPassphrase });
    await store.save({ dek, config: { ...config, active } });
    await emit();
  }

  /**
   * Génère une clé de récupération de remplacement (exige la phrase actuelle).
   * L'ancienne reste valable pour les envois jusqu'à `confirmRecoveryKeyStored()`.
   */
  static async beginRecoveryKeyReplacement(currentPassphrase: string): Promise<{ recoveryKey: string }> {
    const store = getHybridKeyStore();
    const { dek, config } = await store.load();
    if (!dek || !config?.active) throw new HybridCryptoError('BAD_INPUT', 'Sauvegarde chiffrée non configurée.');
    if (!(await HybridBackupConfigService.verifyPassphrase(currentPassphrase))) {
      throw new HybridCryptoError('WRONG_SECRET', 'Phrase secrète actuelle incorrecte.');
    }
    const recovery = generateRecoveryKey();
    const pending = await rotateRecoveryKeyWraps(config.active, { dek, newRecoveryKey: recovery });
    await store.save({ dek, config: { ...config, pending } });
    const recoveryKey = await formatRecoveryKey(recovery);
    await emit();
    return { recoveryKey };
  }
}
